import { useEffect } from 'react';
import { toast } from 'sonner';
import { Copy, Link as LinkIcon, Envelope, PaperPlaneTilt, CodeBlock } from '@phosphor-icons/react';

const SITE = process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com';

const BLURBS = {
  short: `Go Convert Documents — a free, no-signup online converter with 29+ tools including PDF, Word, Excel, PowerPoint, image conversion, OCR, grammar correction and Sign PDF. Files auto-delete after 1 hour. ${SITE}`,
  medium: `Looking for a truly free document converter that respects your privacy? Go Convert Documents offers 29+ tools (PDF ⇄ Word/Excel/PowerPoint/JPG, OCR, merge, split, compress, protect, watermark, grammar-correct, e-sign) with zero signup, no watermark, and files auto-deleted from the servers after 1 hour. Runs in any modern browser. ${SITE}`,
  long: `Go Convert Documents (${SITE}) is a free, open-web alternative to paid document converters like iLovePDF and Smallpdf. It ships 29+ tools — including PDF-to-Word, Word-to-PDF, Excel-to-PDF, OCR for scanned PDFs, merge / split / compress / rotate PDF, password-protect, watermark, grammar check, and a browser-based PDF signature stamper. Every conversion runs on their own servers (no third-party APIs), files are deleted within one hour, no account or credit card is required, and the entire tool works on mobile browsers. A great fit for anyone comparing free PDF tools, iLovePDF alternatives, or Smallpdf alternatives in 2026.`,
};

const EMAIL_TEMPLATE = `Hi [Editor Name],

I just came across your roundup of free PDF tools on [site name] — really useful piece.

I recently launched Go Convert Documents (${SITE}), a genuinely free document converter with 29+ tools including PDF-to-Word, Excel-to-PDF, OCR, grammar check, and PDF e-signing. Everything runs anonymously in the browser, files auto-delete in 1 hour, and there's no watermark or paywall — unlike most competitors.

If you're refreshing the list, I'd love to be considered. Here's the URL: ${SITE}

Happy to send you any screenshots, a demo GIF or a quick 90-second Loom of the workflow — just let me know.

Thanks either way,
[Your name]`;

const HTML_EMBED = `<a href="${SITE}" rel="follow" target="_blank">
  <img src="${SITE}/logo.png" alt="Go Convert Documents" width="120" />
  <span>Convert with Go Convert Documents — free forever</span>
</a>`;

const BADGE_EMBED = `<a href="${SITE}" rel="follow" target="_blank" style="display:inline-block;padding:6px 12px;border:2px solid #0a0a0a;background:#facc15;color:#0a0a0a;font-family:monospace;text-decoration:none;font-weight:700">
  Powered by Go Convert Documents
</a>`;

const IFRAME_EMBED = `<iframe src="${SITE}/embed/pdf-to-word"
  width="100%" height="640"
  frameborder="0" style="border:2px solid #0a0a0a"
  title="PDF to Word — Go Convert Documents"></iframe>`;

function copy(text) {
  const ok = () => toast.success('Copied to clipboard');
  const fail = () => toast.error('Copy failed — please select and copy manually');
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(ok).catch(fail);
    } else {
      // Legacy fallback for older browsers / non-HTTPS
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const success = document.execCommand('copy');
      document.body.removeChild(ta);
      success ? ok() : fail();
    }
  } catch (e) {
    fail();
  }
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="border-2 border-black bg-white p-6 brutal-shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-9 h-9 grid place-items-center border-2 border-black bg-yellow-300">
          <Icon weight="bold" size={18} />
        </span>
        <p className="font-display font-semibold text-lg">{title}</p>
      </div>
      {children}
    </div>
  );
}

export default function Partners() {
  useEffect(() => {
    document.title = 'Partners & Media Kit — Free PDF Converter | Go Convert Documents';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content',
      'Free press-ready assets to link to Go Convert Documents — ready-to-copy blurbs, logo, embed codes, and outreach email templates for bloggers and roundup writers.');
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Partners &amp; Media Kit</p>
      <h1 data-testid="partners-title" className="font-display font-bold text-4xl md:text-5xl leading-tight text-slate-950 mt-2">
        Link to us — the easy kit
      </h1>
      <p className="text-lg text-zinc-700 mt-4 leading-relaxed">
        If you write about PDF tools, iLovePDF alternatives, or productivity software, this page gives you everything you need in one click. Copy any blurb, screenshot the logo, embed the widget on your blog, or send a plug-and-play email to your editor.
      </p>

      <div className="grid gap-6 mt-10">
        <Card title="Ready-to-copy blurbs" icon={LinkIcon}>
          {Object.entries(BLURBS).map(([len, text]) => (
            <div key={len} className="border-2 border-black p-4 mt-3 first:mt-0" data-testid={`blurb-${len}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">{len} ({text.length} chars)</span>
                <button
                  data-testid={`copy-blurb-${len}`}
                  onClick={() => copy(text)}
                  className="brutal-btn secondary text-xs inline-flex items-center gap-1"
                >
                  <Copy weight="bold" size={14} /> Copy
                </button>
              </div>
              <p className="text-sm text-zinc-800 leading-relaxed">{text}</p>
            </div>
          ))}
        </Card>

        <Card title="Outreach email template (for editors of PDF-tool roundups)" icon={Envelope}>
          <textarea
            data-testid="email-template"
            readOnly
            value={EMAIL_TEMPLATE}
            className="w-full h-64 p-3 border-2 border-black font-mono-plex text-sm bg-zinc-50"
          />
          <div className="flex gap-2 mt-3">
            <button
              data-testid="copy-email"
              onClick={() => copy(EMAIL_TEMPLATE)}
              className="brutal-btn secondary inline-flex items-center gap-2"
            >
              <Copy weight="bold" size={16} /> Copy email
            </button>
            <a
              data-testid="mailto-link"
              href={`mailto:?subject=Free%20PDF%20tool%20for%20your%20roundup&body=${encodeURIComponent(EMAIL_TEMPLATE)}`}
              className="brutal-btn inline-flex items-center gap-2"
            >
              <PaperPlaneTilt weight="bold" size={16} /> Open in mail
            </a>
          </div>
        </Card>

        <Card title="Text-badge embed (paste in your site footer)" icon={CodeBlock}>
          <pre data-testid="badge-embed" className="bg-zinc-50 border-2 border-black p-3 text-xs font-mono-plex overflow-x-auto">{BADGE_EMBED}</pre>
          <button
            data-testid="copy-badge"
            onClick={() => copy(BADGE_EMBED)}
            className="brutal-btn secondary text-xs inline-flex items-center gap-1 mt-3"
          >
            <Copy weight="bold" size={14} /> Copy code
          </button>
          <div className="mt-4">
            <span dangerouslySetInnerHTML={{ __html: BADGE_EMBED }} />
          </div>
        </Card>

        <Card title="Logo + link embed (with our brand mark)" icon={CodeBlock}>
          <pre data-testid="html-embed" className="bg-zinc-50 border-2 border-black p-3 text-xs font-mono-plex overflow-x-auto">{HTML_EMBED}</pre>
          <button
            data-testid="copy-html"
            onClick={() => copy(HTML_EMBED)}
            className="brutal-btn secondary text-xs inline-flex items-center gap-1 mt-3"
          >
            <Copy weight="bold" size={14} /> Copy code
          </button>
        </Card>

        <Card title="Embed a full converter widget on your site" icon={CodeBlock}>
          <p className="text-sm text-zinc-700 mb-3">
            Drop the converter directly into your blog post or SaaS. Change the last URL segment to any tool id: <code className="bg-zinc-100 px-1">pdf-to-word</code>, <code className="bg-zinc-100 px-1">merge-pdf</code>, <code className="bg-zinc-100 px-1">sign-pdf</code>, etc.
          </p>
          <pre data-testid="iframe-embed" className="bg-zinc-50 border-2 border-black p-3 text-xs font-mono-plex overflow-x-auto">{IFRAME_EMBED}</pre>
          <button
            data-testid="copy-iframe"
            onClick={() => copy(IFRAME_EMBED)}
            className="brutal-btn secondary text-xs inline-flex items-center gap-1 mt-3"
          >
            <Copy weight="bold" size={14} /> Copy embed
          </button>
        </Card>

        <Card title="Logo download" icon={LinkIcon}>
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Go Convert Documents logo" className="h-16 w-16 border-2 border-black" />
            <a
              href="/logo.png"
              download
              data-testid="download-logo"
              className="brutal-btn secondary inline-flex items-center gap-2"
            >
              Download PNG (transparent)
            </a>
          </div>
        </Card>

        <Card title="Attribution guidelines" icon={LinkIcon}>
          <ul className="list-disc pl-5 text-sm text-zinc-800 space-y-2 leading-relaxed">
            <li>Attribution isn't required, but a <code className="bg-zinc-100 px-1">rel="follow"</code> link back to <a className="underline" href={SITE}>{SITE}</a> is greatly appreciated and helps keep the service free.</li>
            <li>Please use the brand name <strong>"Go Convert Documents"</strong> (three words, capital G, C, D).</li>
            <li>Do not modify the logo colours or add drop shadows — feel free to shrink or crop to a square.</li>
            <li>Screenshots of the app UI are fine to publish without permission.</li>
            <li>Questions? Reach us via the <a className="underline" href="/contact">contact page</a>.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
